/**
 * One factory for every query key in the app.
 *
 * Centralizing them is what makes targeted invalidation practical: a mutation can
 * invalidate `queryKeys.users.detail(id)` without knowing which components are
 * mounted, and `queryKeys.users.all` invalidates the whole subtree. The legacy app
 * had no equivalent, which is why most mutations there just refetched everything.
 *
 * Convention: `all` is the prefix for a domain, `lists()` for any paginated list,
 * and `list(params)` for one specific filtered page.
 */

type Params = Record<string, unknown>

function domain<const T extends string>(name: T) {
    return {
        all: [name] as const,
        lists: () => [name, 'list'] as const,
        list: (params: Params) => [name, 'list', params] as const,
        details: () => [name, 'detail'] as const,
        detail: (id: string) => [name, 'detail', id] as const
    }
}

export const queryKeys = {
    session: ['session'] as const,

    users: {
        ...domain('users'),
        wallet: (userId: string) => ['users', 'wallet', userId] as const,
        inventory: (userId: string) => ['users', 'inventory', userId] as const,
        logins: (userId: string, params: Params) => ['users', 'logins', userId, params] as const,
        bans: (userId: string, params: Params) => ['users', 'bans', userId, params] as const,
        caseHistory: (userId: string, params: Params) => ['users', 'case-history', userId, params] as const,
        packHistory: (userId: string, params: Params) => ['users', 'pack-history', userId, params] as const,
        cryptoAddresses: (userId: string) => ['users', 'crypto-addresses', userId] as const
    },

    transactions: {
        ...domain('transactions'),
        summary: (params: Params) => ['transactions', 'summary', params] as const
    },

    timeline: {
        all: ['timeline'] as const,
        roots: (params: Params) => ['timeline', 'roots', params] as const,
        expand: (params: Params) => ['timeline', 'expand', params] as const,
        resolve: (params: Params) => ['timeline', 'resolve', params] as const
    },

    deposits: {
        ...domain('deposits'),
        summary: (params: Params) => ['deposits', 'summary', params] as const
    },

    withdrawals: {
        ...domain('withdrawals'),
        missingCards: (params: Params) => ['withdrawals', 'missing-cards', params] as const
    },

    cards: {
        ...domain('cards'),
        search: (params: Params) => ['cards', 'search', params] as const,
        one: (id: string) => ['cards', 'one', id] as const,
        rarities: (params: Params) => ['cards', 'rarities', params] as const,
        setCodes: (params: Params) => ['cards', 'set-codes', params] as const,
        catalogue: (params: Params) => ['cards', 'catalogue', params] as const
    },

    duplicates: {
        ...domain('duplicates'),
        scans: (params: Params) => ['duplicates', 'scans', params] as const
    },

    pricing: {
        ...domain('pricing')
    },

    packs: {
        all: ['packs'] as const,
        blueprints: (params: Params) => ['packs', 'blueprints', params] as const,
        blueprint: (slug: string) => ['packs', 'blueprints', 'detail', slug] as const,
        live: (params: Params) => ['packs', 'live', params] as const,
        categories: () => ['packs', 'categories'] as const
    },

    cases: {
        ...domain('cases')
    },

    promo: {
        ...domain('promo')
    },

    affiliates: {
        ...domain('affiliates'),
        stats: (userId: string, params: Params) => ['affiliates', 'stats', userId, params] as const,
        earners: (userId: string, params: Params) => ['affiliates', 'earners', userId, params] as const
    },

    // Both endpoints return their whole collection in one unpaginated response, so
    // there is no `list(params)` variant to key on.
    settings: {
        all: ['settings'] as const,
        list: () => ['settings', 'list'] as const
    },

    maintenance: {
        all: ['maintenance'] as const,
        list: () => ['maintenance', 'list'] as const
    },

    // One key per endpoint rather than one per page, because the stats dashboard
    // asks for the same six series twice -- once for the selected range and once
    // for the preceding one -- and the two must not share a cache entry.
    stats: {
        all: ['stats'] as const,
        registrations: (params: Params) => ['stats', 'registrations', params] as const,
        transactions: (params: Params) => ['stats', 'transactions', params] as const,
        wagers: (params: Params) => ['stats', 'wagers', params] as const,
        payments: (params: Params) => ['stats', 'payments', params] as const,
        affiliates: (params: Params) => ['stats', 'affiliates', params] as const,
        online: (params: Params) => ['stats', 'online', params] as const
    },

    // Inbox lanes sit under their own domain's prefix, so the invalidation a mutation
    // already does -- `withdrawals.all` after a move, `maintenance.all` after a flip --
    // refreshes the badge with it. `queue` rather than `list`, so a socket refetch of
    // the open withdrawals list does not drag every lane along.
    queues: {
        domain: (domain: QueueDomain) => [domain, 'queue'] as const,
        source: (domain: QueueDomain, id: string) => [domain, 'queue', id] as const
    }
} as const

type QueueDomain = 'withdrawals' | 'deposits' | 'duplicates' | 'maintenance'
